import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Tooltip } from "react-leaflet";
import L from "leaflet";
import { ArrowLeft, RefreshCw, MapPin, Bird, Calendar, ExternalLink, Search, X, Cloud, CloudRain, CloudLightning, Sun, CloudSun } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useBackToHome } from "./lib/use-back";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SINGAPORE_CENTER = [1.3521, 103.8198];
const SINGAPORE_ZOOM = 11;
const SKELETON_KEYS = ["sk-1", "sk-2", "sk-3", "sk-4"];
const MAP_STYLE = { height: "100%", width: "100%" };

const forecastColor = (f) => {
  const s = (f || "").toLowerCase();
  if (s.includes("thundery")) return "#5A2D9E";
  if (s.includes("heavy") && (s.includes("rain") || s.includes("shower")))
    return "#2A4C8F";
  if (s.includes("rain") || s.includes("shower")) return "#4A82D8";
  if (s.includes("hazy") || s.includes("haze") || s.includes("mist") || s.includes("fog"))
    return "#B0A08A";
  if (s.includes("partly")) return "#E7DDB8";
  if (s.includes("cloudy")) return "#A89B85";
  if (s.includes("fair") || s.includes("sunny") || s.includes("warm")) return "#F4C542";
  return "#A89B85";
};

const ForecastIcon = ({ forecast, size = 14 }) => {
  const s = (forecast || "").toLowerCase();
  const props = { size, strokeWidth: 1.8 };
  if (s.includes("thundery")) return <CloudLightning {...props} />;
  if (s.includes("rain") || s.includes("shower")) return <CloudRain {...props} />;
  if (s.includes("cloudy") && !s.includes("partly")) return <Cloud {...props} />;
  if (s.includes("partly")) return <CloudSun {...props} />;
  if (s.includes("fair") || s.includes("sunny") || s.includes("warm"))
    return <Sun {...props} />;
  return <Cloud {...props} />;
};

function makeIcon(speciesCount, opts = {}) {
  const { rare = false } = opts;
  // Size correlates to number of species in the cluster (4 tiers).
  let sz;
  if (speciesCount <= 1) sz = 30;
  else if (speciesCount <= 3) sz = 40;
  else if (speciesCount <= 7) sz = 52;
  else sz = 64;
  const cls = rare
    ? "bird-marker bird-marker--rare"
    : "bird-marker bird-marker--normal";
  return L.divIcon({
    html: `<div class="${cls}" style="width:${sz}px;height:${sz}px;font-size:${sz >= 50 ? 14 : 12}px;">${speciesCount}</div>`,
    className: "bird-marker-wrapper",
    iconSize: [sz, sz],
    iconAnchor: [sz / 2, sz / 2],
    popupAnchor: [0, -sz / 2],
  });
}

const Header = ({ onRefresh, refreshing, lastRefreshed }) => {
  const nav = useNavigate();
  return (
    <header
      data-testid="map-header"
      className="sticky top-0 z-30 backdrop-blur-xl bg-[#FAF9F6]/80 border-b border-[#E5E2DA]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <button
          data-testid="map-back-button"
          onClick={() => nav("/")}
          className="inline-flex items-center gap-2 text-[#2C5530] hover:text-[#1E3A21] font-mono text-xs tracking-widest uppercase"
        >
          <ArrowLeft size={14} /> Back to index
        </button>
        <div className="flex items-center gap-3">
          <div
            className="hidden md:flex items-center gap-2 font-mono text-xs text-[#5C5C5C]"
            data-testid="map-last-refreshed"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#4A7C59] animate-pulse" />
            refreshed · {lastRefreshed || "—"}
          </div>
          <Button
            data-testid="map-refresh-button"
            onClick={onRefresh}
            disabled={refreshing}
            variant="outline"
            className="border-[#2C5530] text-[#2C5530] hover:bg-[#2C5530] hover:text-white rounded-full px-5"
          >
            <RefreshCw size={14} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </div>
    </header>
  );
};

const SpeciesObservationsSheet = ({ species, observations, loading, onClose }) => (
  <Sheet open={!!species} onOpenChange={(o) => !o && onClose()}>
    <SheetContent
      side="right"
      className="z-[1500] bg-[#FAF9F6] border-l border-[#E5E2DA] w-full sm:max-w-md p-0 overflow-y-auto"
      data-testid="species-observations-sheet"
    >
      <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E5E2DA] text-left">
        <div className="label-eyebrow mb-2">All sightings · last 30 days</div>
        <SheetTitle
          data-testid="species-observations-title"
          className="font-serif-display text-3xl font-light tracking-tight text-[#1A1A1A]"
        >
          {species?.common_name || "—"}
        </SheetTitle>
        {species?.scientific_name && (
          <SheetDescription className="font-serif-display italic text-sm text-[#5C5C5C]">
            {species.scientific_name}
          </SheetDescription>
        )}
        <div className="font-mono text-xs text-[#8C7A6B] mt-2">
          {loading
            ? "Loading…"
            : `${observations.length} observation${observations.length === 1 ? "" : "s"} across Singapore`}
        </div>
      </SheetHeader>
      <div className="px-6 py-6 space-y-3">
        {loading ? (
          SKELETON_KEYS.map((k) => (
            <div key={k} className="field-card p-4 space-y-2">
              <div className="h-4 w-3/4 shimmer rounded" />
              <div className="h-3 w-1/2 shimmer rounded" />
            </div>
          ))
        ) : observations.length === 0 ? (
          <p className="text-sm text-[#5C5C5C]">
            No recent observations of this species in Singapore.
          </p>
        ) : (
          observations.map((o, i) => (
            <div
              key={`${o.sub_id}-${o.obs_dt}-${o.loc_id}`}
              data-testid={`species-obs-${i}`}
              className="field-card p-4"
            >
              <div className="flex items-start gap-2 text-sm">
                <Calendar size={14} className="text-[#8C7A6B] mt-0.5 flex-shrink-0" strokeWidth={1.8} />
                <div className="font-mono text-xs text-[#1A1A1A]">{o.obs_dt}</div>
              </div>
              <div className="flex items-start gap-2 text-sm mt-2">
                <MapPin size={14} className="text-[#8C7A6B] mt-0.5 flex-shrink-0" strokeWidth={1.8} />
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${o.lat},${o.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#1A1A1A] hover:text-[#2C5530] hover:underline"
                >
                  {o.loc_name || "Unknown location"}
                </a>
              </div>
              {o.how_many && (
                <div className="font-mono text-xs text-[#8C7A6B] mt-2">
                  ×{o.how_many} observed
                </div>
              )}
              {o.checklist_url && (
                <a
                  href={o.checklist_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#2C5530] hover:text-[#1E3A21] mt-2"
                >
                  Checklist <ExternalLink size={11} />
                </a>
              )}
            </div>
          ))
        )}
      </div>
    </SheetContent>
  </Sheet>
);

export default function MonthlyBirdMapPage() {
  useBackToHome();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [activeSpecies, setActiveSpecies] = useState(null);
  const [activeObs, setActiveObs] = useState([]);
  const [obsLoading, setObsLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const mapRef = useRef(null);

  const [weatherOn, setWeatherOn] = useState(false);
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);

  const loadWeather = async () => {
    setWeatherLoading(true);
    try {
      const r = await axios.get(`${API}/weather-nowcast`, { timeout: 20000 });
      setWeatherData(r.data);
    } catch {
      // weather overlay is optional; map still works without it
    } finally {
      setWeatherLoading(false);
    }
  };

  const toggleWeather = async () => {
    const next = !weatherOn;
    setWeatherOn(next);
    if (next && !weatherData) await loadWeather();
  };

  const [rareCodes, setRareCodes] = useState(new Set());

  const loadRareSet = async () => {
    try {
      const r = await axios.get(`${API}/rare-sightings`, {
        params: { sid: "SN36214", days: 7 },
        timeout: 25000,
      });
      const codes = new Set(
        (r.data?.sightings || [])
          .map((s) => s.species_code)
          .filter(Boolean)
      );
      setRareCodes(codes);
    } catch {
      // rare highlights are optional
    }
  };

  // Flatten unique species (by species_code) across all clusters with their
  // best (most recent) location, used both for search and for fly-to behaviour.
  const speciesIndex = useMemo(() => {
    const map = new Map();
    for (const cl of data?.clusters || []) {
      for (const sp of cl.species || []) {
        const key = sp.species_code || sp.common_name;
        if (!key) continue;
        const existing = map.get(key);
        if (!existing || (sp.latest_obs_dt || "") > (existing.latest_obs_dt || "")) {
          map.set(key, {
            species_code: sp.species_code,
            common_name: sp.common_name,
            scientific_name: sp.scientific_name,
            latest_obs_dt: sp.latest_obs_dt,
            lat: cl.lat,
            lng: cl.lng,
            loc_name: cl.loc_name,
          });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.common_name || "").localeCompare(b.common_name || "")
    );
  }, [data]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return speciesIndex
      .filter(
        (s) =>
          (s.common_name || "").toLowerCase().includes(q) ||
          (s.scientific_name || "").toLowerCase().includes(q)
      )
      .slice(0, 12);
  }, [searchQuery, speciesIndex]);

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/monthly-birds`, {
        params: { region: "SG", back: 30 },
        timeout: 40000,
      });
      setData(r.data);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Could not reach eBird right now. Try again in a moment."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(() => {
      if (cancelled) return;
      load(false);
      loadRareSet();
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const openSpeciesObservations = async (sp) => {
    setActiveSpecies(sp);
    setActiveObs([]);
    if (!sp?.species_code) return;
    setObsLoading(true);
    try {
      const r = await axios.get(
        `${API}/monthly-birds/species/${encodeURIComponent(sp.species_code)}`,
        { params: { region: "SG", back: 30 }, timeout: 30000 }
      );
      setActiveObs(r.data?.observations || []);
    } catch {
      setActiveObs([]);
    } finally {
      setObsLoading(false);
    }
  };

  const handleSearchSelect = (sp) => {
    setSearchQuery("");
    setSearchFocused(false);
    if (sp?.lat && sp?.lng && mapRef.current) {
      mapRef.current.flyTo([sp.lat, sp.lng], 15, { duration: 0.8 });
    }
    openSpeciesObservations(sp);
  };

  // Per-cluster rare flag (any species in cluster matches the rare codes set)
  const clusterIsRare = useMemo(() => {
    const map = new Map();
    if (!rareCodes || rareCodes.size === 0) return map;
    for (const cl of data?.clusters || []) {
      const has = (cl.species || []).some((sp) =>
        sp.species_code && rareCodes.has(sp.species_code)
      );
      if (has) map.set(cl.key, true);
    }
    return map;
  }, [data, rareCodes]);

  const weatherMarkers = useMemo(
    () =>
      (weatherData?.areas || []).map((a) => {
        const color = forecastColor(a.forecast);
        return {
          ...a,
          center: [a.lat, a.lng],
          pathOptions: { color, weight: 1, opacity: 0.35, fillColor: color, fillOpacity: 0.4 },
        };
      }),
    [weatherData]
  );

  const clusterMarkers = useMemo(
    () =>
      (data?.clusters || []).map((cl) => ({
        cl,
        position: [cl.lat, cl.lng],
        icon: makeIcon(cl.species_count, { rare: clusterIsRare.get(cl.key) === true }),
      })),
    [data, clusterIsRare]
  );

  const lastRefreshed = useMemo(() => {
    if (!data?.fetched_at) return null;
    const d = new Date(data.fetched_at);
    if (isNaN(d.getTime())) return null;
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    return `${Math.floor(mins / 60)} h ago`;
  }, [data]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
      <Header onRefresh={() => load(true)} refreshing={refreshing} lastRefreshed={lastRefreshed} />

      <section className="border-b border-[#E5E2DA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-6">
          <div className="label-eyebrow mb-3 flex items-center gap-2">
            <Bird size={12} strokeWidth={1.8} className="text-[#2C5530]" />
            From eBird Singapore · last 30 days
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A1A1A] leading-[1.05]">
            Monthly bird <em className="italic text-[#2C5530]">map</em>.
          </h1>
          <p className="mt-4 text-base text-[#5C5C5C] max-w-2xl">
            Every species observed in Singapore this month, mapped to where it
            was seen. Select a pin to view all species sighted at that
            location; select a species to view every place it was recorded.
          </p>

          <div className="mt-6 grid grid-cols-3 gap-6 font-mono text-xs max-w-xl">
            <div>
              <div className="label-eyebrow mb-1">Locations</div>
              <div
                data-testid="map-locations-count"
                className="text-[#1A1A1A] text-2xl font-medium"
              >
                {data ? data.total_locations : "—"}
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Unique species</div>
              <div
                data-testid="map-species-count"
                className="text-[#1A1A1A] text-2xl font-medium"
              >
                {data ? data.total_species : "—"}
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Observations</div>
              <div className="text-[#1A1A1A] text-2xl font-medium">
                {data ? data.total_observations : "—"}
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 lg:px-10 py-8">
        {error ? (
          <div
            data-testid="map-error"
            className="border border-[#E5C2BA] bg-[#FBEFEC] text-[#7A2E1F] p-6 rounded-sm font-mono text-sm"
          >
            {error}
          </div>
        ) : (
          <>
          {/* Weather 2-hr nowcast toggle — sits above the map so it never blocks it */}
          <div
            data-testid="weather-controls"
            className="mb-4 flex flex-wrap items-center gap-3"
          >
            <button
              type="button"
              data-testid="weather-toggle"
              onClick={toggleWeather}
              aria-pressed={weatherOn}
              className={`inline-flex items-center gap-2 rounded-full h-10 px-4 font-mono text-xs tracking-wider uppercase font-medium transition-colors ${
                weatherOn
                  ? "bg-[#2C5530] text-[#FAF9F6] hover:bg-[#1E3A21]"
                  : "bg-[#FAF9F6] text-[#1A1A1A] border border-[#E5E2DA] hover:border-[#2C5530] hover:text-[#2C5530]"
              }`}
            >
              <Cloud size={14} strokeWidth={1.8} />
              {weatherLoading
                ? "Loading…"
                : weatherOn
                ? "Weather 2-hr nowcast · on"
                : "Weather 2-hr nowcast"}
            </button>
            {weatherOn && weatherData?.valid_period?.text && (
              <div
                data-testid="weather-legend"
                className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-widest text-[#1A1A1A]"
              >
                <span className="label-eyebrow text-[#8C7A6B]">
                  Valid {weatherData.valid_period.text}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#F4C542" }} />
                  Fair
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#E7DDB8" }} />
                  Partly cloudy
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#A89B85" }} />
                  Cloudy
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#4A82D8" }} />
                  Rain
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#2A4C8F" }} />
                  Heavy rain
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#5A2D9E" }} />
                  Thundery
                </span>
                <span className="text-[9px] normal-case tracking-normal text-[#8C7A6B]">
                  NEA · data.gov.sg
                </span>
              </div>
            )}
          </div>

          <div
            data-testid="bird-map-container"
            className="relative rounded-sm overflow-hidden border border-[#E5E2DA]"
            style={{ height: "70vh", minHeight: 500 }}
          >
            {/* Species search overlay — positioned above the Leaflet panes */}
            <div
              data-testid="map-search-overlay"
              className="absolute top-4 left-4 z-[500] w-[320px] max-w-[calc(100%-32px)]"
            >
              <div className="bg-[#FAF9F6] border border-[#E5E2DA] rounded-full shadow-lg flex items-center pl-4 pr-2 h-11">
                <Search size={15} strokeWidth={1.8} className="text-[#5C5C5C] flex-shrink-0" />
                <input
                  data-testid="map-species-search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                  placeholder={`Search ${speciesIndex.length || ""} species on the map…`}
                  className="flex-1 bg-transparent px-3 h-full outline-none text-sm placeholder:text-[#8C7A6B] text-[#1A1A1A]"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                    className="p-1.5 rounded-full hover:bg-[#EFEBE0] text-[#5C5C5C]"
                    data-testid="map-search-clear"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              {searchFocused && searchQuery && (
                <div
                  data-testid="map-search-results"
                  className="mt-2 bg-[#FAF9F6] border border-[#E5E2DA] rounded-sm shadow-xl max-h-80 overflow-y-auto"
                >
                  {searchResults.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-[#5C5C5C] font-mono text-xs">
                      No species match &ldquo;{searchQuery}&rdquo;.
                    </div>
                  ) : (
                    searchResults.map((sp, i) => {
                      const spRare =
                        sp.species_code && rareCodes && rareCodes.has(sp.species_code);
                      return (
                      <button
                        key={sp.species_code || sp.common_name}
                        type="button"
                        data-testid={`map-search-result-${i}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSearchSelect(sp)}
                        className="w-full text-left px-4 py-2.5 hover:bg-[#EFEBE0] border-b border-[#E5E2DA] last:border-b-0 transition-colors"
                      >
                        <div
                          className={`text-sm leading-tight ${
                            spRare ? "font-bold text-[#C17767]" : "text-[#1A1A1A]"
                          }`}
                        >
                          {sp.common_name}
                        </div>
                        {sp.scientific_name && (
                          <div className="font-serif-display italic text-xs text-[#5C5C5C] mt-0.5">
                            {sp.scientific_name}
                          </div>
                        )}
                        <div className="font-mono text-[10px] tracking-widest uppercase text-[#8C7A6B] mt-1">
                          {sp.loc_name || "Unknown"} · {sp.latest_obs_dt || ""}
                        </div>
                      </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {loading && (
              <div className="absolute inset-0 z-[400] flex items-center justify-center bg-[#FAF9F6]/85 font-mono text-sm text-[#5C5C5C]">
                <RefreshCw size={16} className="mr-2 animate-spin" /> Loading map…
              </div>
            )}

            <MapContainer
              center={SINGAPORE_CENTER}
              zoom={SINGAPORE_ZOOM}
              minZoom={10}
              maxZoom={18}
              scrollWheelZoom
              zoomControl={false}
              style={MAP_STYLE}
              ref={mapRef}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &middot; data by <a href="https://ebird.org">eBird</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {weatherOn &&
                weatherMarkers.map((a) => {
                  return (
                    <CircleMarker
                      key={`w-${a.area}`}
                      center={a.center}
                      radius={26}
                      pathOptions={a.pathOptions}
                      interactive
                    >
                      <Tooltip
                        direction="top"
                        opacity={0.98}
                        sticky
                        className="!bg-[#FAF9F6] !border-[#E5E2DA] !text-[#1A1A1A]"
                      >
                        <div className="text-xs">
                          <div className="font-medium flex items-center gap-1.5">
                            <ForecastIcon forecast={a.forecast} /> {a.area}
                          </div>
                          <div className="text-[#5C5C5C]">{a.forecast}</div>
                        </div>
                      </Tooltip>
                    </CircleMarker>
                  );
                })}
              {clusterMarkers.map(({ cl, position, icon }, idx) => {
                return (
                <Marker
                  key={cl.key || idx}
                  position={position}
                  icon={icon}
                >
                  <Popup>
                    <div className="min-w-[220px]">
                      <div className="font-serif-display text-lg leading-tight text-[#1A1A1A]">
                        {cl.loc_name || "Unknown location"}
                      </div>
                      <div className="font-mono text-[10px] tracking-widest uppercase text-[#8C7A6B] mt-1">
                        {cl.species_count} species · {cl.observation_count} sighting
                        {cl.observation_count === 1 ? "" : "s"}
                      </div>
                      <ul
                        data-testid={`cluster-species-list-${idx}`}
                        className="mt-3 space-y-1.5 max-h-60 overflow-y-auto pr-1"
                      >
                        {cl.species.map((sp, j) => {
                          const spRare =
                            sp.species_code && rareCodes && rareCodes.has(sp.species_code);
                          return (
                          <li key={sp.species_code || sp.common_name}>
                            <button
                              type="button"
                              data-testid={`cluster-species-btn-${idx}-${j}`}
                              onClick={() => openSpeciesObservations(sp)}
                              className={`text-left w-full text-sm hover:text-[#2C5530] hover:underline decoration-[#C17767] ${
                                spRare ? "font-bold text-[#C17767]" : "text-[#1A1A1A]"
                              }`}
                            >
                              {sp.common_name}
                              <span className="font-mono text-[10px] text-[#8C7A6B] ml-2 font-normal">
                                {sp.latest_obs_dt || ""}
                              </span>
                            </button>
                          </li>
                          );
                        })}
                      </ul>
                    </div>
                  </Popup>
                </Marker>
                );
              })}
            </MapContainer>
          </div>
          </>
        )}
      </main>

      <SpeciesObservationsSheet
        species={activeSpecies}
        observations={activeObs}
        loading={obsLoading}
        onClose={() => setActiveSpecies(null)}
      />
    </div>
  );
}
